import React from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Divider, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  styled
} from '@mui/material';
import { formatCurrency, numberToWords } from '../../utils/formatters';
import { AkMoInvoicingFormatProps } from './AkMoInvoicingFormat';

// Styled Components with clean Zoho-style structure
const InvoicePaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  paddingBottom: theme.spacing(4),
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  width: '210mm', // A4 width
  backgroundColor: '#fff',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  minHeight: '297mm', // A4 height exactly
  boxSizing: 'border-box',
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  '@media print': {
    boxShadow: 'none',
    width: '210mm',
    minHeight: '297mm',
    padding: 0,
    margin: 0,
    pageBreakAfter: 'avoid',
    pageBreakInside: 'avoid',
  }
}));

const ZohoAkMoA4Template: React.FC<AkMoInvoicingFormatProps> = ({
  businessDetails,
  clientDetails,
  deliveryDetails,
  invoiceDetails,
  items,
  primaryColor = '#4776E6',
  fontColor = '#333333',
}) => {
  const finalDeliveryDetails = deliveryDetails || {
    name: clientDetails.name,
    address: clientDetails.address,
    city: clientDetails.city,
    state: clientDetails.state,
    pincode: clientDetails.pincode,
    phone: clientDetails.phone,
    email: clientDetails.email,
  };

  const calculateTotals = () => {
    const totals = items.reduce((acc, item) => {
      return {
        totalTaxableValue: acc.totalTaxableValue + item.taxableValue,
        totalTaxAmount: acc.totalTaxAmount + item.taxAmount,
        totalAmount: acc.totalAmount + item.total,
      };
    }, { totalTaxableValue: 0, totalTaxAmount: 0, totalAmount: 0 });
    
    if (invoiceDetails.discount) totals.totalAmount -= invoiceDetails.discount;
    const additionalChargesTotal = invoiceDetails.additionalCharges?.reduce((sum, charge) => sum + charge.amount, 0) || 0;
    totals.totalAmount += additionalChargesTotal;
    
    return { ...totals, additionalChargesTotal };
  };
  
  const totals = calculateTotals();

  const isInterStateSupply = () => {
    if (!businessDetails.gstin || !clientDetails.gstin) return false;
    const sellerStateCode = businessDetails.gstin.substring(0, 2);
    const buyerStateCode = clientDetails.gstin.substring(0, 2);
    return sellerStateCode !== buyerStateCode;
  };

  const isInterState = isInterStateSupply();

  const calculateGST = () => {
    if (isInterState) return { cgst: 0, sgst: 0, igst: totals.totalTaxAmount };
    return { cgst: totals.totalTaxAmount / 2, sgst: totals.totalTaxAmount / 2, igst: 0 };
  };

  const gstValues = calculateGST();

  return (
    <InvoicePaper>
      <Box sx={{ flex: '1 0 auto', color: fontColor, fontSize: '0.875rem' }}>
        {/* Header - Clean Zoho Style */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
          <Box sx={{ maxWidth: '50%' }}>
            {businessDetails.logoUrl && (
              <Box mb={2}>
                 <img src={businessDetails.logoUrl} alt="Logo" style={{ maxHeight: '60px', maxWidth: '200px' }} />
              </Box>
            )}
            <Typography variant="h6" fontWeight="bold" sx={{ color: '#000', mb: 1 }}>
              {businessDetails.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {businessDetails.address}, {businessDetails.city}<br />
              {businessDetails.state} - {businessDetails.pincode}
            </Typography>
            {businessDetails.gstin && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                GSTIN: {businessDetails.gstin}
              </Typography>
            )}
          </Box>

          <Box sx={{ textAlign: 'right', minWidth: '35%' }}>
            <Typography variant="h4" fontWeight="300" sx={{ color: '#000', mb: 2, letterSpacing: '2px', textTransform: 'uppercase' }}>
              Tax Invoice
            </Typography>
            <Table size="small" sx={{ width: '100%', '& td, & th': { borderBottom: 'none', py: 0.5, px: 0 } }}>
              <TableBody>
                <TableRow>
                  <TableCell align="right" sx={{ color: 'text.secondary', width: '50%' }}>Invoice#:</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', color: '#000' }}>{invoiceDetails.invoiceNumber}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align="right" sx={{ color: 'text.secondary' }}>Invoice Date:</TableCell>
                  <TableCell align="right">{invoiceDetails.invoiceDate}</TableCell>
                </TableRow>
                {invoiceDetails.dueDate && (
                  <TableRow>
                    <TableCell align="right" sx={{ color: 'text.secondary' }}>Due Date:</TableCell>
                    <TableCell align="right">{invoiceDetails.dueDate}</TableCell>
                  </TableRow>
                )}
                {invoiceDetails.purchaseRefNumber && (
                  <TableRow>
                    <TableCell align="right" sx={{ color: 'text.secondary' }}>PO#:</TableCell>
                    <TableCell align="right">{invoiceDetails.purchaseRefNumber}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </Box>

        <Divider sx={{ mb: 4, borderColor: '#e0e0e0' }} />

        {/* Addresses */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
          <Box sx={{ width: '45%' }}>
            <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 'bold' }}>Bill To</Typography>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ color: primaryColor, mb: 0.5 }}>
              {clientDetails.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {clientDetails.address}<br />
              {clientDetails.city}, {clientDetails.state} {clientDetails.pincode}
            </Typography>
            {clientDetails.gstin && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <strong>GSTIN:</strong> {clientDetails.gstin}
              </Typography>
            )}
          </Box>

          <Box sx={{ width: '45%' }}>
            <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 'bold' }}>Ship To</Typography>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ color: primaryColor, mb: 0.5 }}>
              {finalDeliveryDetails.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {finalDeliveryDetails.address}<br />
              {finalDeliveryDetails.city}, {finalDeliveryDetails.state} {finalDeliveryDetails.pincode}
            </Typography>
          </Box>
        </Box>

        {/* Item Table */}
        <TableContainer sx={{ mb: 4 }}>
          <Table size="small" sx={{ 
            '& th': { borderBottom: `2px solid ${primaryColor}`, color: '#000', fontWeight: 'bold', py: 1.5, px: 1 },
            '& td': { borderBottom: '1px solid #eee', py: 1.5, px: 1, color: fontColor }
          }}>
            <TableHead>
              <TableRow>
                <TableCell width="5%">#</TableCell>
                <TableCell width="35%">Item & Description</TableCell>
                <TableCell width="10%" align="center">HSN/SAC</TableCell>
                <TableCell width="10%" align="right">Qty</TableCell>
                <TableCell width="15%" align="right">Rate</TableCell>
                <TableCell width="10%" align="right">Tax</TableCell>
                <TableCell width="15%" align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">{item.productName}</Typography>
                    {item.description && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        {item.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">{item.hsn}</TableCell>
                  <TableCell align="right">{item.quantity} {item.unit || ''}</TableCell>
                  <TableCell align="right">{formatCurrency(item.taxableValue / item.quantity)}</TableCell>
                  <TableCell align="right">{item.gstRate}%</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(item.total)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Totals & Bank Details */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
          <Box sx={{ width: '55%' }}>
            {businessDetails.bankName && (
              <Box sx={{ bgcolor: '#f9f9f9', p: 2, borderRadius: 1, border: '1px solid #eee' }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: primaryColor }}>
                  Bank Details
                </Typography>
                <Box display="flex" alignItems="center">
                  <Box flex={1}>
                    <Table size="small" sx={{ '& td': { borderBottom: 'none', py: 0.5, px: 0 } }}>
                      <TableBody>
                    <TableRow>
                      <TableCell sx={{ color: 'text.secondary', width: '30%' }}>Bank:</TableCell>
                      <TableCell sx={{ fontWeight: 'medium' }}>{businessDetails.bankName}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ color: 'text.secondary' }}>Account #:</TableCell>
                      <TableCell sx={{ fontWeight: 'medium' }}>{businessDetails.accountNumber}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ color: 'text.secondary' }}>IFSC:</TableCell>
                      <TableCell sx={{ fontWeight: 'medium' }}>{businessDetails.ifsc}</TableCell>
                    </TableRow>
                    {businessDetails.upiId && (
                      <TableRow>
                        <TableCell sx={{ color: 'text.secondary' }}>UPI ID:</TableCell>
                        <TableCell sx={{ fontWeight: 'medium' }}>{businessDetails.upiId}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
              
              {/* Dynamic UPI QR Code */}
              {businessDetails.upiId && (
                <Box sx={{ ml: 2, textAlign: 'center', bgcolor: '#fff', p: 1, borderRadius: 1, border: '1px solid #eee' }}>
                  <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 'bold', color: primaryColor }}>Scan to Pay</Typography>
                  <img 
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(`upi://pay?pa=${businessDetails.upiId}&pn=${encodeURIComponent(businessDetails.name)}&am=${totals.totalAmount}&cu=INR`)}`} 
                    alt="UPI QR Code" 
                    style={{ width: '80px', height: '80px' }} 
                  />
                </Box>
              )}
            </Box>
          </Box>
            )}
            
            <Box mt={3}>
              <Typography variant="body2" color="text.secondary">Total in Words:</Typography>
              <Typography variant="body2" fontWeight="medium" sx={{ textTransform: 'capitalize' }}>
                Rupees {numberToWords(Math.round(totals.totalAmount))} Only
              </Typography>
            </Box>
          </Box>

          <Box sx={{ width: '40%' }}>
            <Table size="small" sx={{ '& td, & th': { py: 1, px: 1 } }}>
              <TableBody>
                <TableRow>
                  <TableCell align="right" sx={{ borderBottom: '1px solid #eee', color: 'text.secondary' }}>Sub Total</TableCell>
                  <TableCell align="right" sx={{ borderBottom: '1px solid #eee', fontWeight: 'medium' }}>
                    {formatCurrency(totals.totalTaxableValue)}
                  </TableCell>
                </TableRow>
                {invoiceDetails.discount && invoiceDetails.discount > 0 && (
                  <TableRow>
                    <TableCell align="right" sx={{ borderBottom: '1px solid #eee', color: 'text.secondary' }}>Discount</TableCell>
                    <TableCell align="right" sx={{ borderBottom: '1px solid #eee' }}>
                      -{formatCurrency(invoiceDetails.discount)}
                    </TableCell>
                  </TableRow>
                )}
                {totals.additionalChargesTotal > 0 && (
                  <TableRow>
                    <TableCell align="right" sx={{ borderBottom: '1px solid #eee', color: 'text.secondary' }}>Additional Charges</TableCell>
                    <TableCell align="right" sx={{ borderBottom: '1px solid #eee' }}>
                      {formatCurrency(totals.additionalChargesTotal)}
                    </TableCell>
                  </TableRow>
                )}
                {isInterState ? (
                  <TableRow>
                    <TableCell align="right" sx={{ borderBottom: '1px solid #eee', color: 'text.secondary' }}>IGST</TableCell>
                    <TableCell align="right" sx={{ borderBottom: '1px solid #eee' }}>{formatCurrency(gstValues.igst)}</TableCell>
                  </TableRow>
                ) : (
                  <>
                    <TableRow>
                      <TableCell align="right" sx={{ borderBottom: 'none', color: 'text.secondary' }}>CGST</TableCell>
                      <TableCell align="right" sx={{ borderBottom: 'none' }}>{formatCurrency(gstValues.cgst)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell align="right" sx={{ borderBottom: '1px solid #eee', color: 'text.secondary' }}>SGST</TableCell>
                      <TableCell align="right" sx={{ borderBottom: '1px solid #eee' }}>{formatCurrency(gstValues.sgst)}</TableCell>
                    </TableRow>
                  </>
                )}
                <TableRow sx={{ bgcolor: `${primaryColor}10` }}>
                  <TableCell align="right" sx={{ borderBottom: 'none', fontWeight: 'bold', color: primaryColor, fontSize: '1rem' }}>Total</TableCell>
                  <TableCell align="right" sx={{ borderBottom: 'none', fontWeight: 'bold', color: primaryColor, fontSize: '1rem' }}>
                    {formatCurrency(totals.totalAmount)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        </Box>

        {/* Notes & Terms */}
        <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <Box sx={{ width: '60%' }}>
            {invoiceDetails.termsAndConditions && invoiceDetails.termsAndConditions.length > 0 && (
              <Box mb={2}>
                <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 'bold' }}>
                  Terms & Conditions
                </Typography>
                <ul style={{ margin: 0, paddingLeft: '16px', color: 'text.secondary', fontSize: '0.75rem' }}>
                  {invoiceDetails.termsAndConditions.map((term, index) => (
                    <li key={index}>{term}</li>
                  ))}
                </ul>
              </Box>
            )}
          </Box>
          <Box sx={{ width: '35%', textAlign: 'center' }}>
            <Typography variant="subtitle2" sx={{ mb: 4, color: 'text.secondary' }}>
              For {businessDetails.name}
            </Typography>
            <Divider sx={{ mb: 1, mx: 2 }} />
            <Typography variant="caption" color="text.secondary">
              Authorized Signatory
            </Typography>
          </Box>
        </Box>
      </Box>
    </InvoicePaper>
  );
};

export default ZohoAkMoA4Template;
